import React, { useState, useContext, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { addWordToUserVocabulary } from "../../../redux/slices/userVocabularySlice";
import {
  fetchWords,
  setSearchTerm,
  clearResults,
} from "../../../redux/slices/searchSlice";
import {
  getArticlesByTopic,
  getArticleByName,
} from "../../../utils/getArticles"; // Utility function for fetching articles by category
import { clearActiveExercise } from "../../../redux/slices/exerciseSlice";
import { UserContext } from "../../user/UserContext";
import "bootstrap/dist/css/bootstrap.min.css"; // Importing Bootstrap for styling
import "./ReadArticle.css"; // Importing custom CSS for additional styles

const ReadArticle = () => {
  const dispatch = useDispatch(); // Redux dispatch for triggering actions
  const { userAttributes } = useContext(UserContext); // Accessing user attributes via context
  const { results } = useSelector((state) => state.search); // Extracting search results from Redux state

  // State variables
  const [showPopup, setShowPopup] = useState(false); // Determines if the pop-up is visible
  const [popupPosition, setPopupPosition] = useState({ top: 0, left: 0 }); // Tracks the position of the pop-up
  const [popupContent, setPopupContent] = useState([]); // Stores translations to display in the pop-up
  const [hoveredWord, setHoveredWord] = useState(""); // The word currently being hovered
  const [searchTimeout, setSearchTimeout] = useState(null); // Timeout for delayed search execution
  const [isHoveringOverPopup, setIsHoveringOverPopup] = useState(false); // Tracks if the user is hovering over the pop-up
  const [addedWords, setAddedWords] = useState([]); // List of words added to the vocabulary
  const [selectedCategory, setSelectedCategory] = useState(null); // Tracks the selected category
  const [articles, setArticles] = useState([]); // Stores the fetched articles for the selected category
  const [selectedArticle, setSelectedArticle] = useState(null); // Tracks the selected article
  const [isArticleSelected, setIsArticleSelected] = useState(false); // Tracks if an article has been selected
  const popupRef = useRef(null); // Reference to the pop-up for positioning and measurement

  // Handle when the user selects a category
  const handleCategorySelect = async (category) => {
    setSelectedCategory(category); // Save the selected category
    // Fetch articles for the selected category
    try {
      const fetchedArticles = await getArticlesByTopic(category); // Fetch articles for the category

      setArticles(fetchedArticles);
    } catch (error) {
      console.error("Failed to fetch articles:", error);
    }
  };

  // Handle when an article is selected
  const handleArticleSelect = async (article) => {
    setIsArticleSelected(true); // Mark that an article is selected

    try {
      const selectedArticle = await getArticleByName(article);
      setSelectedArticle(selectedArticle); // Set the selected article to display its content
    } catch (error) {
      console.error("Failed to fetch article content:", error);
    }
  };

  // Handle when the mouse enters a word
  const handleMouseEnter = (e, word) => {
    const cleanedWord = word.replace(/[.,?!:;"'“”‘’]/g, ""); // Clean punctuation from the hovered word
    setHoveredWord(cleanedWord); // Set the hovered word in state

    setPopupContent(["searching..."]); // Show "searching..." in the pop-up initially
    dispatch(setSearchTerm(cleanedWord)); // Dispatch the cleaned word as the search term

    if (searchTimeout) {
      clearTimeout(searchTimeout); // Clear any previous search timeout
    }

    dispatch(fetchWords({ term: cleanedWord, triggerTranslate: false })); // Fetch translations without auto-translate

    const timeoutId = setTimeout(() => {
      dispatch(fetchWords({ term: cleanedWord, triggerTranslate: true })); // Fetch translations with auto-translate
    }, 300);
    setSearchTimeout(timeoutId); // Store the timeout ID in state

    // Determine the position of the pop-up
    const { top, left, width, height } = e.target.getBoundingClientRect();
    const popupHeight = popupRef.current ? popupRef.current.offsetHeight : 50; // Estimate pop-up height if not rendered
    const spaceAbove = top;
    const spaceBelow = window.innerHeight - (top + height);

    if (spaceAbove < popupHeight + 10 && spaceBelow > popupHeight + 10) {
      setPopupPosition({ top: top + height + 10, left: left + width / 2 }); // Place the pop-up below the word
    } else {
      setPopupPosition({ top: top - popupHeight - 10, left: left + width / 2 }); // Place the pop-up above the word
    }

    setShowPopup(true); // Show the pop-up
  };

  // Update the pop-up content when search results arrive
  useEffect(() => {
    if (results.length > 0) {
      setPopupContent(results.map((result) => result.translation)); // Map results to translations for display
      if (searchTimeout) {
        clearTimeout(searchTimeout); // Clear the timeout to prevent unnecessary calls
      }
    }
  }, [results, searchTimeout]);

  // Handle when the mouse leaves a word
  const handleMouseLeave = () => {
    if (!isHoveringOverPopup) {
      setShowPopup(false); // Hide the pop-up
      if (searchTimeout) {
        clearTimeout(searchTimeout); // Clear the timeout
      }
      dispatch(clearResults()); // Clear search results
      dispatch(setSearchTerm("")); // Clear search term
    }
  };

  // Prevent the pop-up from disappearing when hovered
  const handlePopupMouseEnter = () => setIsHoveringOverPopup(true); // Keep pop-up visible
  const handlePopupMouseLeave = () => {
    setIsHoveringOverPopup(false); // Allow the pop-up to hide
    setShowPopup(false);
    dispatch(clearResults());
    dispatch(setSearchTerm(""));
  };

  // Handle adding a word to the user's vocabulary
  const handleAddWord = async () => {
    try {
      const result = await dispatch(
        addWordToUserVocabulary({
          word: hoveredWord,
          translation: popupContent.join(", "),
          owner: userAttributes.sub,
        })
      );
      console.log("Word added to user vocabulary:", result);

      setAddedWords((prevWords) => [
        ...prevWords,
        { word: hoveredWord, translation: popupContent.join(", ") },
      ]);
    } catch (error) {
      console.error("Failed to add word:", error);
    }
  };

  if (!selectedCategory) {
    // Render the category selection screen if no category is selected
    return (
      <div className="full-screen-container text-center">
        <h2>Select a Category</h2>
        <div className="d-flex justify-content-center">
          <button
            className="btn btn-primary mx-2"
            onClick={() => handleCategorySelect("Politika")}
          >
            Politika
          </button>
          <button
            className="btn btn-secondary mx-2"
            onClick={() => handleCategorySelect("Biznis")}
          >
            Biznis
          </button>
        </div>
      </div>
    );
  }

  if (!isArticleSelected) {
    // Render the list of articles if an article is not selected yet
    return (
      <div className="full-screen-container">
        <h2 style={{ textAlign: "center" }}>Articles for {selectedCategory}</h2>{" "}
        {/* Header centered */}
        <ol style={{ textAlign: "left", marginLeft: "20px" }}>
          {" "}
          {/* List aligned to the left */}
          {articles.map((article, index) => (
            <li
              key={index}
              className="article-item"
              style={{
                cursor: "pointer",
                marginBottom: "10px", // Optional for spacing between items
              }}
              onClick={() => handleArticleSelect(article)}
            >
              {article}
            </li>
          ))}
        </ol>
      </div>
    );
  }

  return (
    <div className="full-screen-container">
      <div className="main-content">
        <div className="article-section">
          <h2 className="text-center">Article</h2>
          <p>
            {/* Display the article content */}
            {selectedArticle ? (
              selectedArticle.split(" ").map((word, index) => (
                <span
                  key={index}
                  className="hover-container"
                  onMouseEnter={(e) => handleMouseEnter(e, word)} // Trigger hover logic
                  onMouseLeave={handleMouseLeave} // Handle mouse leave
                  onClick={handleAddWord} // Add word to vocabulary on click
                >
                  {word}{" "}
                </span>
              ))
            ) : (
              <p>Loading article...</p>
            )}
          </p>
        </div>
        <div className="added-words-section">
          <h2 className="text-center">Added Words</h2>
          <ul>
            {addedWords.map((entry, index) => (
              <li key={index}>
                <strong>{entry.word}</strong>: {entry.translation}
              </li>
            ))}
          </ul>
        </div>
      </div>
      <div className="additional-field text-end">
        <button
          className="btn btn-primary"
          onClick={() => dispatch(clearActiveExercise())} // Clear the current exercise on click
        >
          Finish
        </button>
      </div>
      {showPopup && (
        <div
          ref={popupRef} // Reference the pop-up for positioning
          className="popup"
          style={{
            position: "absolute",
            top: `${popupPosition.top}px`, // Set top position dynamically
            left: `${popupPosition.left}px`, // Set left position dynamically
            display: "flex",
            alignItems: "center",
            flexDirection: "column", // Stack translations vertically
          }}
          onMouseEnter={handlePopupMouseEnter} // Keep the pop-up visible on hover
          onMouseLeave={handlePopupMouseLeave} // Allow hiding the pop-up on leave
        >
          {popupContent.map((translation, index) => (
            <div key={index}>{translation}</div> // Render each translation
          ))}
        </div>
      )}
    </div>
  );
};

export default ReadArticle;
